import axios from 'axios';
import { InternalAxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../utils/constants';
import type { StediMultiExportFile } from '../utils/exportStediResponsesFormat';
import type {
  AdminSigninRequest,
  AdminAuthResponse,
  AdminSessionResponse,
  AdminCostSummaryResponse,
  AdminCallCostListResponse,
  AdminCallCostRowListResponse,
  AdminCostSyncRequest,
  AdminCostSyncResponse,
  AdminRevenueSummaryResponse,
  AdminOrgMarginsResponse,
  AdminOrgBillingOverviewResponse,
  AdminPlanListResponse,
  AdminPlanResponse,
  AdminPlanUpdateRequest,
  AdminDataHygieneReportResponse,
  AdminDataHygieneReconcileRequest,
  AdminDataHygieneReconcileResponse,
  PerformanceBillingConfigListResponse,
  PerformanceBillingConfigResponse,
  PerformanceBillingConfigUpsertRequest,
  PerformanceBillingPreviewRequest,
  PerformanceBillingPreviewResponse,
  PerformanceBillingGenerateInvoiceRequest,
  PerformanceBillingInvoiceResponse,
  PerformanceBillingInvoiceListResponse,
  AdminTrafficResponse,
  AdminAnalyticsSummaryResponse,
  AdminOrgStatsResponse,
  AgentPerformanceSummaryResponse,
  AgentNotetakerQualityResponse,
  AgentBrainQueryHealthResponse,
  AgentNodeMetricsResponse,
  AgentErrorsResponse,
  AgentIncompleteCallsResponse,
  AgentLatencyResponse,
  AgentRecentCallsResponse,
  AgentCallExportResponse,
  AdminOrganizationListResponse,
  AdminOrganizationResponse,
  AdminOrganizationCreateRequest,
  AdminOrganizationUpdateRequest,
  AdminUserListResponse,
  AdminUserResponse,
  AdminUserCreateRequest,
  AdminBlockOrganizationRequest,
  AdminWelcomeEmailRequest,
  AdminWelcomeEmailResponse,
  BulkUploadClaimsResponse,
  CallFlowTemplate,
  OrgWorkflowConfig,
  CallStateTraceItem,
  StateMetrics,
  ABMetrics,
  VoiceAgent,
  VoiceAgentCreatePayload,
  VoiceAgentUpdatePayload,
  ClaimFieldSchema,
  ClaimFieldSchemaUpdatePayload,
  CreatePatientRequest,
  UpdatePatientRequest,
  CreatePatientResponse,
  GetPatientsResponse,
  CreatePayerRequest,
  PayerUpdatePayload,
  CreatePayerResponse,
  GetPayersResponse,
  CreateProviderRequest,
  UpdateProviderRequest,
  ProviderResponse as TProviderResponse,
  GetProvidersResponse,
  GetCallsResponse,
} from './types';

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetryRequest = (error: unknown): boolean => {
  const axiosErr = error as AxiosError | undefined;
  if (!axiosErr) return false;
  if (axiosErr.code === 'ECONNABORTED') return true;
  if (!axiosErr.response) return true;
  const status = axiosErr.response.status;
  return status >= 500 && status < 600;
};

async function withGetRetry<T>(request: () => Promise<T>, attempts = 2): Promise<T> {
  let lastError: unknown;
  for (let i = 1; i <= attempts; i += 1) {
    try {
      return await request();
    } catch (error) {
      lastError = error;
      if (i >= attempts || !shouldRetryRequest(error)) {
        throw error;
      }
      await sleep(300 * i);
    }
  }
  throw lastError;
}

adminApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = sessionStorage.getItem('adminAuthToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

adminApi.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('adminAuthToken');
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const adminSignin = async (data: AdminSigninRequest): Promise<AdminAuthResponse> => {
  const response = await adminApi.post('/admin/auth/signin', data);
  return response.data;
};

export const adminSession = async (): Promise<AdminSessionResponse> => {
  const response = await adminApi.get('/admin/auth/session');
  return response.data;
};

export const adminInspectEmailAction = async (data: { oob_code: string }) => {
  const response = await adminApi.post('/admin/auth/email-action/inspect', data);
  return response.data;
};

export const adminApplyEmailAction = async (data: { oob_code: string }) => {
  const response = await adminApi.post('/admin/auth/email-action/apply', data);
  return response.data;
};

export const adminConfirmResetPassword = async (data: {
  oob_code: string;
  new_password: string;
}) => {
  const response = await adminApi.post('/admin/auth/reset-password/confirm', data);
  return response.data;
};

export const getAdminCostSummary = async (params?: {
  org_id?: string;
  provider?: string;
  start_date?: string;
  end_date?: string;
}): Promise<AdminCostSummaryResponse> => {
  const response = await withGetRetry(() => adminApi.get('/admin/costs/summary', { params }));
  return response.data;
};

export const getAdminCallCosts = async (params?: {
  page?: number;
  limit?: number;
  org_id?: string;
  provider?: string;
  call_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<AdminCallCostListResponse> => {
  const response = await adminApi.get('/admin/costs/calls', { params });
  return response.data;
};

export const getAdminCallCostRows = async (params?: {
  page?: number;
  limit?: number;
  org_id?: string;
  provider?: string;
  call_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<AdminCallCostRowListResponse> => {
  const response = await withGetRetry(() => adminApi.get('/admin/costs/call-rows', { params }));
  return response.data;
};

export const syncAdminCosts = async (data: AdminCostSyncRequest): Promise<AdminCostSyncResponse> => {
  const response = await adminApi.post('/admin/costs/sync', data);
  return response.data;
};

export const getAdminRevenueSummary = async (params?: {
  start_date?: string;
  end_date?: string;
}): Promise<AdminRevenueSummaryResponse> => {
  const response = await adminApi.get('/admin/billing/revenue-summary', { params });
  return response.data;
};

export const getAdminMargins = async (params?: {
  start_date?: string;
  end_date?: string;
}): Promise<AdminOrgMarginsResponse> => {
  const response = await adminApi.get('/admin/billing/margins', { params });
  return response.data;
};

export const getAdminOrgBillingOverview = async (orgId: string): Promise<AdminOrgBillingOverviewResponse> => {
  const response = await adminApi.get(`/admin/billing/org/${orgId}/overview`);
  return response.data;
};

export const getAdminPlans = async (): Promise<AdminPlanListResponse> => {
  const response = await adminApi.get('/admin/billing/plans');
  return response.data;
};

export const updateAdminPlan = async (
  planId: string,
  data: AdminPlanUpdateRequest
): Promise<AdminPlanResponse> => {
  const response = await adminApi.patch(`/admin/billing/plans/${planId}`, data);
  return response.data;
};

export const getAdminDataHygieneReport = async (params?: {
  org_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}): Promise<AdminDataHygieneReportResponse> => {
  const response = await adminApi.get('/admin/billing/data-hygiene/report', { params });
  return response.data;
};

export const runAdminDataHygieneReconcile = async (
  data: AdminDataHygieneReconcileRequest
): Promise<AdminDataHygieneReconcileResponse> => {
  const response = await adminApi.post('/admin/billing/data-hygiene/reconcile-calls', data);
  return response.data;
};

export const listPerformanceBillingConfigs = async (params?: {
  org_id?: string;
  active_only?: boolean;
}): Promise<PerformanceBillingConfigListResponse> => {
  const response = await adminApi.get('/admin/billing/performance/configs', { params });
  return response.data;
};

export const upsertPerformanceBillingConfig = async (
  data: PerformanceBillingConfigUpsertRequest
): Promise<PerformanceBillingConfigResponse> => {
  const response = await adminApi.post('/admin/billing/performance/configs', data);
  return response.data;
};

export const previewPerformanceBillingInvoice = async (
  data: PerformanceBillingPreviewRequest
): Promise<PerformanceBillingPreviewResponse> => {
  const response = await adminApi.post('/admin/billing/performance/preview', data);
  return response.data;
};

export const generatePerformanceBillingInvoice = async (
  data: PerformanceBillingGenerateInvoiceRequest
): Promise<PerformanceBillingInvoiceResponse> => {
  const response = await adminApi.post('/admin/billing/performance/generate-invoice', data);
  return response.data;
};

export const listPerformanceBillingInvoices = async (params?: {
  org_id?: string;
  limit?: number;
}): Promise<PerformanceBillingInvoiceListResponse> => {
  const response = await adminApi.get('/admin/billing/performance/invoices', { params });
  return response.data;
};

// Admin Analytics API
export const getAdminAnalyticsTraffic = async (params?: {
  hours?: number;
  org_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<AdminTrafficResponse> => {
  const response = await withGetRetry(() => adminApi.get('/admin/analytics/traffic', { params }));
  return response.data;
};

export const getAdminAnalyticsSummary = async (): Promise<AdminAnalyticsSummaryResponse> => {
  const response = await withGetRetry(() => adminApi.get('/admin/analytics/summary'));
  return response.data;
};

export const getAdminOrgStats = async (): Promise<AdminOrgStatsResponse> => {
  const response = await withGetRetry(() => adminApi.get('/admin/analytics/organizations'));
  return response.data;
};

type AgentPerfBaseParams = {
  org_id?: string;
  start_date?: string;
  end_date?: string;
  call_type?: string;
  /** Free-text filter: claim number / org name (substring) or call_id (UUID). */
  q?: string;
};

const agentPerfParams = (params?: AgentPerfBaseParams) => {
  const p: Record<string, string> = {};
  if (params?.org_id) p.org_id = params.org_id;
  if (params?.start_date) p.start_date = params.start_date;
  if (params?.end_date) p.end_date = params.end_date;
  if (params?.call_type) p.call_type = params.call_type;
  if (params?.q && params.q.trim()) p.q = params.q.trim();
  return p;
};

export const getAgentPerformanceSummary = async (
  params?: AgentPerfBaseParams,
): Promise<AgentPerformanceSummaryResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/summary', { params: agentPerfParams(params) })
  );
  return response.data;
};

export const getAgentPerformanceNotetaker = async (
  params?: AgentPerfBaseParams,
): Promise<AgentNotetakerQualityResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/notetaker-quality', { params: agentPerfParams(params) })
  );
  return response.data;
};

export const getAgentPerformanceBrainQuery = async (
  params?: AgentPerfBaseParams,
): Promise<AgentBrainQueryHealthResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/brain-query-health', { params: agentPerfParams(params) })
  );
  return response.data;
};

export const getAgentPerformanceNodes = async (
  params?: AgentPerfBaseParams,
): Promise<AgentNodeMetricsResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/nodes', { params: agentPerfParams(params) })
  );
  return response.data;
};

export const getAgentPerformanceErrors = async (
  params?: AgentPerfBaseParams & { limit?: number; offset?: number },
): Promise<AgentErrorsResponse> => {
  const p: Record<string, string | number> = { ...agentPerfParams(params) };
  if (params?.limit != null) p.limit = params.limit;
  if (params?.offset != null) p.offset = params.offset;
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/errors', { params: p })
  );
  return response.data;
};

export const getAgentPerformanceIncompleteCalls = async (
  params?: AgentPerfBaseParams & { limit?: number; offset?: number },
): Promise<AgentIncompleteCallsResponse> => {
  const p: Record<string, string | number> = { ...agentPerfParams(params) };
  if (params?.limit != null) p.limit = params.limit;
  if (params?.offset != null) p.offset = params.offset;
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/incomplete-calls', { params: p })
  );
  return response.data;
};

export const getAgentPerformanceLatency = async (
  params?: AgentPerfBaseParams,
): Promise<AgentLatencyResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/latency', { params: agentPerfParams(params) })
  );
  return response.data;
};

export const getAgentPerformanceCalls = async (
  params?: AgentPerfBaseParams & {
    sort?: string;
    order?: string;
    limit?: number;
    offset?: number;
    node_id?: string;
    path_filter?: string;
  },
): Promise<AgentRecentCallsResponse> => {
  const p: Record<string, string | number> = { ...agentPerfParams(params) };
  if (params?.sort) p.sort = params.sort;
  if (params?.order) p.order = params.order;
  if (params?.limit != null) p.limit = params.limit;
  if (params?.offset != null) p.offset = params.offset;
  if (params?.node_id) p.node_id = params.node_id;
  if (params?.path_filter) p.path_filter = params.path_filter;
  const response = await withGetRetry(() =>
    adminApi.get('/admin/analytics/agent-performance/calls', { params: p })
  );
  return response.data;
};

export const getAgentPerformanceCallExport = async (callId: string): Promise<AgentCallExportResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/analytics/agent-performance/calls/${callId}/export`)
  );
  return response.data;
};

// Admin workflow APIs (Decision Tree + DT analytics)
export const getAdminWorkflowTemplates = async (): Promise<CallFlowTemplate[]> => {
  const response = await withGetRetry(() => adminApi.get('/admin/workflow/templates'));
  return response.data;
};

export const getAdminWorkflowTemplate = async (templateId: string): Promise<CallFlowTemplate> => {
  const response = await withGetRetry(() => adminApi.get(`/admin/workflow/templates/${templateId}`));
  return response.data;
};

export const createAdminWorkflowTemplate = async (
  payload: Partial<CallFlowTemplate>
): Promise<CallFlowTemplate> => {
  const response = await adminApi.post('/admin/workflow/templates', payload);
  return response.data;
};

export const updateAdminWorkflowTemplate = async (
  templateId: string,
  payload: Partial<CallFlowTemplate>
): Promise<CallFlowTemplate> => {
  const response = await adminApi.patch(`/admin/workflow/templates/${templateId}`, payload);
  return response.data;
};

export const publishAdminWorkflowTemplate = async (templateId: string): Promise<void> => {
  await adminApi.post(`/admin/workflow/templates/${templateId}/publish`);
};

export const rollbackAdminWorkflowTemplate = async (templateId: string): Promise<void> => {
  await adminApi.post(`/admin/workflow/templates/${templateId}/rollback`);
};

export const updateTemplatePositions = async (
  templateId: string,
  positions: Array<{ node_id: string; x: number; y: number }>
): Promise<CallFlowTemplate> => {
  const response = await adminApi.patch(`/admin/workflow/templates/${templateId}/positions`, {
    positions,
  });
  return response.data;
};

export const duplicateTemplate = async (
  templateId: string,
  name: string
): Promise<CallFlowTemplate> => {
  const response = await adminApi.post(`/admin/workflow/templates/${templateId}/duplicate`, {
    name,
  });
  return response.data;
};

export const seedAdminDefaultWorkflowTemplate = async (): Promise<{
  skipped: boolean;
  template_id: string;
  nodes: number;
  edges: number;
  groups: number;
}> => {
  const response = await adminApi.post('/admin/workflow/templates/seed-default');
  return response.data;
};

export const getAdminWorkflowOrgConfig = async (orgId: string): Promise<OrgWorkflowConfig> => {
  const response = await withGetRetry(() => adminApi.get(`/admin/workflow/orgs/${orgId}/config`));
  return response.data;
};

export const updateAdminWorkflowOrgConfig = async (
  orgId: string,
  payload: Partial<OrgWorkflowConfig>
): Promise<OrgWorkflowConfig> => {
  const response = await adminApi.patch(`/admin/workflow/orgs/${orgId}/config`, payload);
  return response.data;
};

// Live-resolved system prompt for the org's current call_mode. Returns
// whatever the runtime would actually ship (legacy: VoiceAgent.system_prompt
// or platform default; PSM: prebuilt YAML body). Never falls back across
// modes -- if a YAML is missing we get `source: 'missing'` and an explanation.
export interface AdminResolvedSystemPrompt {
  call_mode:
    | 'legacy'
    | 'prompt_state_machine'
    | 'prompt_state_machine_v3';
  source:
    | 'voice_agent.system_prompt'
    | 'platform_default'
    | 'psm_yaml'
    // ``psm_override`` indicates the org has a non-empty
    // ``psm_prompt_override`` and live calls will use it instead of
    // the YAML body. The endpoint still ships the YAML body in
    // ``prompt`` so the admin UI can show both.
    | 'psm_override'
    | 'decision_tree'
    | 'missing'
    | 'error'
    | null;
  prompt: string | null;
  template_id?: string;
  template_name?: string;
  template_version?: number;
  nodes_count?: number;
  edges_count?: number;
  voice_agent_id?: string;
  voice_agent_name?: string;
  yaml_path?: string;
  yaml_generated_at?: string;
  states_count?: number;
  estimated_tokens?: number;
  compact?: boolean;
  platform_default_path?: string;
  message?: string;
  error?: string;
  // Per-org dynamic agent name (added 2026-05-04, migration 064).
  // ``agent_name`` is the raw value from org_workflow_configs (NULL if
  // never customised); ``effective_agent_name`` is the resolved string
  // the runtime will substitute (defaults to "Viraj").
  agent_name?: string | null;
  effective_agent_name?: string;
  // PSM-only: the per-org override body (NULL if not set) and the
  // string that will actually ship on the next call. ``effective_prompt``
  // == ``psm_prompt_override`` when set, else ``prompt`` (YAML body).
  psm_prompt_override?: string | null;
  effective_prompt?: string;
}

export const getAdminWorkflowResolvedPrompt = async (
  orgId: string,
): Promise<AdminResolvedSystemPrompt> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/workflow/orgs/${orgId}/resolved-system-prompt`),
  );
  return response.data;
};

// Shared Voice Agents catalog (admin-managed). Single source of truth post-062
// for which agent dials calls — the per-org `voice_provider_configs` table and
// its CRUD endpoints were retired by the same migration.
export const listAdminVoiceAgents = async (): Promise<VoiceAgent[]> => {
  const response = await withGetRetry(() => adminApi.get('/admin/voice-agents'));
  return response.data;
};

export const getAdminVoiceAgent = async (agentId: string): Promise<VoiceAgent> => {
  const response = await withGetRetry(() => adminApi.get(`/admin/voice-agents/${agentId}`));
  return response.data;
};

export const createAdminVoiceAgent = async (
  payload: VoiceAgentCreatePayload
): Promise<VoiceAgent> => {
  const response = await adminApi.post('/admin/voice-agents', payload);
  return response.data;
};

export const updateAdminVoiceAgent = async (
  agentId: string,
  payload: VoiceAgentUpdatePayload
): Promise<VoiceAgent> => {
  const response = await adminApi.patch(`/admin/voice-agents/${agentId}`, payload);
  return response.data;
};

export const deleteAdminVoiceAgent = async (agentId: string): Promise<void> => {
  await adminApi.delete(`/admin/voice-agents/${agentId}`);
};

// Fork a (possibly shared) voice agent into a per-org clone and reassign
// the org's voice_agent_id to the clone. Idempotent: returns the source
// untouched if it's already exclusive to this org. Used by the org's
// Workflow tab so editing a shared agent's prompt/template never bleeds
// into other orgs' configurations.
export const forkVoiceAgentForOrg = async (
  agentId: string,
  orgId: string,
): Promise<VoiceAgent> => {
  const response = await adminApi.post(
    `/admin/voice-agents/${agentId}/fork-for-org/${orgId}`,
  );
  return response.data;
};

// PSM Tool Configuration (Phase 2 admin panel)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getAdminToolCatalog = async (): Promise<any[]> => {
  const response = await withGetRetry(() => adminApi.get('/admin/voice-agents/tool-catalog'));
  return response.data;
};

export const getAdminVoiceAgentToolConfig = async (
  agentId: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Record<string, Record<string, any>>> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/voice-agents/${agentId}/tool-config`)
  );
  return response.data;
};

export const updateAdminVoiceAgentToolConfig = async (
  agentId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: Record<string, Record<string, any>>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Record<string, any>> => {
  const response = await adminApi.put(
    `/admin/voice-agents/${agentId}/tool-config`,
    payload
  );
  return response.data;
};

// Per-org configurable claim upload schema (admin-side). Mirrors the
// user-side `/org-config/claim-field-schema` endpoint but with org_id in the
// URL so admins can edit on behalf of any org.
export const getAdminOrgClaimFieldSchema = async (
  orgId: string
): Promise<ClaimFieldSchema> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/claim-field-schema`)
  );
  return response.data;
};

export const updateAdminOrgClaimFieldSchema = async (
  orgId: string,
  payload: ClaimFieldSchemaUpdatePayload
): Promise<ClaimFieldSchema> => {
  const response = await adminApi.put(
    `/admin/organizations/${orgId}/claim-field-schema`,
    payload
  );
  return response.data;
};

// Admin-side claim listing + manual dial. Lives outside the user-facing
// claim controller so the bypass on `ai_calling_enabled` cannot leak via
// shared dependency injection.
export interface AdminClaimEdiLabel {
  slug: string;
  label: string;
  color: string;
}

export interface AdminOrgClaimEdiSummary {
  checked_at?: string | null;
  mapped_status?: string | null;
  label?: AdminClaimEdiLabel | null;
  status_category?: string | null;
  status_code_value?: string | null;
  amount_paid?: string | null;
  submitted_amount?: string | null;
  paid_date?: string | null;
  trading_partner_claim_number?: string | null;
}

export interface AdminOrgClaim {
  id: string;
  org_id: string;
  claim_number: string | null;
  status: string;
  call_purpose: string;
  date_of_service: string | null;
  amount_cents: number;
  retry_count: number;
  max_retries: number;
  denial_code: string | null;
  denial_reason: string | null;
  patient_name: string | null;
  payer_name: string | null;
  payer_phone: string | null;
  edi_summary: AdminOrgClaimEdiSummary | null;
  eligibility_verified_at?: string | null;
  last_edi_check_at?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdminOrgClaimListResponse {
  org_id: string;
  ai_calling_enabled: boolean;
  total: number;
  page: number;
  limit: number;
  claims: AdminOrgClaim[];
}

export interface AdminDialResult {
  success: boolean;
  claim_id: string;
  call_id?: string | null;
  twilio_call_sid?: string | null;
  error?: string | null;
}

export interface AdminDialBatchResponse {
  results: AdminDialResult[];
}

export const listAdminOrgClaims = async (
  orgId: string,
  params?: {
    page?: number;
    limit?: number;
    status?: string;
    call_purpose?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }
): Promise<AdminOrgClaimListResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/claims`, { params })
  );
  return response.data;
};

/**
 * Fetch full admin claim detail (with calls + transcripts) for use by
 * `ClaimDetailPanel` in the admin shell.  Mirrors the user-side
 * `getClaimById` shape so the panel can reuse all of its existing
 * rendering logic (AI summary, CallTraceTimeline, etc.).
 */
export const adminGetOrgClaimById = async (
  orgId: string,
  claimId: string
): Promise<{ success: boolean; message: string; data: import('./types').ClaimData }> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/claims/${claimId}`)
  );
  return response.data;
};

export const dialAdminOrgClaim = async (
  orgId: string,
  claimId: string,
  note?: string
): Promise<AdminDialResult> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/claims/${claimId}/dial`,
    note ? { note } : {}
  );
  return response.data;
};

export const dialAdminOrgClaimsBatch = async (
  orgId: string,
  claimIds: string[]
): Promise<AdminDialBatchResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/claims/dial-batch`,
    { claim_ids: claimIds }
  );
  return response.data;
};

export const adminDeleteOrgClaim = async (
  orgId: string,
  claimId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await adminApi.delete(
    `/admin/organizations/${orgId}/claims/${claimId}`
  );
  return response.data;
};

export const adminBulkDeleteOrgClaims = async (
  orgId: string,
  params:
    | { claim_ids: string[] }
    | { all: true; search?: string | null; status?: string | null; call_purpose?: string | null }
): Promise<{
  success: boolean;
  message: string;
  data: {
    deleted_count: number;
    skipped_count: number;
    error_count: number;
    deleted_ids: string[];
    skipped_ids: string[];
    errors: { id: string; error: string }[];
  };
}> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/claims/bulk-delete`,
    params,
    { timeout: 300000 }
  );
  return response.data;
};

// (Removed in 062 cleanup) The legacy multi-provider voice config CRUD
// (`/admin/workflow/voice-providers`, `/admin/workflow/orgs/.../voice-configs`)
// and the per-provider performance dashboard
// (`/admin/analytics/voice-providers`) were retired with the move to
// ElevenLabs-only. Use the VoiceAgent admin endpoints instead.

export const getAdminWorkflowClaimTimeline = async (
  claimId: string,
  orgId?: string
): Promise<CallStateTraceItem[]> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/workflow/claims/${claimId}/timeline`, {
      params: orgId ? { org_id: orgId } : undefined,
    })
  );
  return response.data;
};

export const getAdminWorkflowStateAnalytics = async (params?: {
  org_id?: string;
  payer_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<StateMetrics[]> => {
  const response = await withGetRetry(() => adminApi.get('/admin/workflow/analytics/states', { params }));
  return response.data;
};

export const getAdminWorkflowABAnalytics = async (params?: {
  org_id?: string;
  payer_id?: string;
  start_date?: string;
  end_date?: string;
}): Promise<ABMetrics[]> => {
  const response = await withGetRetry(() => adminApi.get('/admin/workflow/analytics/ab', { params }));
  return response.data;
};

// (Removed in 062 cleanup) Call Splicer (`/admin/splicer/...`) and the
// Feature Testing harness (`/admin/feature-tests/...`) were retired —
// see the migration notes in alembic 062_cleanup_admin_features.

// Admin Organization Management API
export const getAdminOrganizations = async (params?: {
  search?: string;
  blocked_only?: boolean;
  limit?: number;
  offset?: number;
}): Promise<AdminOrganizationListResponse> => {
  const response = await withGetRetry(() => adminApi.get('/admin/organizations', { params }));
  return response.data;
};

export const getAdminOrganization = async (orgId: string): Promise<AdminOrganizationResponse> => {
  const response = await withGetRetry(() => adminApi.get(`/admin/organizations/${orgId}`));
  return response.data;
};

export const createAdminOrganization = async (
  data: AdminOrganizationCreateRequest
): Promise<AdminOrganizationResponse> => {
  const response = await adminApi.post('/admin/organizations', data);
  return response.data;
};

export const updateAdminOrganization = async (
  orgId: string,
  data: AdminOrganizationUpdateRequest
): Promise<AdminOrganizationResponse> => {
  const response = await adminApi.patch(`/admin/organizations/${orgId}`, data);
  return response.data;
};

export const blockAdminOrganization = async (
  orgId: string,
  data: AdminBlockOrganizationRequest
): Promise<AdminOrganizationResponse> => {
  const response = await adminApi.post(`/admin/organizations/${orgId}/block`, data);
  return response.data;
};

export const unblockAdminOrganization = async (orgId: string): Promise<AdminOrganizationResponse> => {
  const response = await adminApi.post(`/admin/organizations/${orgId}/unblock`);
  return response.data;
};

export const deleteAdminOrganization = async (orgId: string): Promise<AdminOrganizationResponse> => {
  const response = await adminApi.delete(`/admin/organizations/${orgId}`);
  return response.data;
};

export const sendAdminOrganizationWelcomeEmail = async (
  orgId: string,
  data: AdminWelcomeEmailRequest
): Promise<AdminWelcomeEmailResponse> => {
  const response = await adminApi.post(`/admin/organizations/${orgId}/welcome-email`, data);
  return response.data;
};

export const getAdminOrganizationUsers = async (orgId: string): Promise<AdminUserListResponse> => {
  const response = await withGetRetry(() => adminApi.get(`/admin/organizations/${orgId}/users`));
  return response.data;
};

export const createAdminOrganizationUser = async (
  orgId: string,
  data: AdminUserCreateRequest
): Promise<AdminUserResponse> => {
  const response = await adminApi.post(`/admin/organizations/${orgId}/users`, data);
  return response.data;
};

export const deactivateAdminUser = async (orgId: string, userId: string): Promise<AdminUserResponse> => {
  const response = await adminApi.patch(`/admin/organizations/${orgId}/users/${userId}/deactivate`);
  return response.data;
};

export const activateAdminUser = async (orgId: string, userId: string): Promise<AdminUserResponse> => {
  const response = await adminApi.patch(`/admin/organizations/${orgId}/users/${userId}/activate`);
  return response.data;
};

export const resendAdminUserWelcome = async (orgId: string, userId: string): Promise<AdminUserResponse> => {
  const response = await adminApi.post(`/admin/organizations/${orgId}/users/${userId}/resend-welcome`);
  return response.data;
};

// ---------------------------------------------------------------------------
// Admin-side claims upload (any org, by URL)
// ---------------------------------------------------------------------------
// Mirrors ``bulkUploadClaims`` in services/api.ts but routes through the
// admin axios instance + admin URL surface. Used by the
// ``/admin/upload-claims`` page so platform admins can push claims to any
// org without an impersonation token.
export interface AdminBulkUploadOptions {
  mappingOverrides?: Record<string, string>;
  dryRun?: boolean;
}

export const adminBulkUploadClaims = async (
  orgId: string,
  file: File,
  options: AdminBulkUploadOptions = {}
): Promise<BulkUploadClaimsResponse> => {
  const form = new FormData();
  form.append('file', file);
  if (options.mappingOverrides && Object.keys(options.mappingOverrides).length > 0) {
    form.append('mapping_overrides', JSON.stringify(options.mappingOverrides));
  }
  if (options.dryRun) {
    form.append('dry_run', 'true');
  }
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/upload-claims`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Large files process synchronously server-side; allow up to 5 min.
      timeout: 300000,
    }
  );
  return response.data;
};

export const adminGetExampleClaimUploadUrl = (orgId: string, format: 'csv' | 'xlsx' = 'csv'): string => {
  return `${API_BASE_URL}/admin/organizations/${orgId}/upload-claims/example?format=${format}`;
};

export const adminStoreClaimUploadBatch = async (
  orgId: string,
  file: File,
  options: { hasHeaderRow?: boolean } = {},
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('has_header_row', String(options.hasHeaderRow ?? true));
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/upload-claims/batches`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const adminListClaimUploadBatches = async (orgId: string, status?: string) => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/upload-claims/batches`,
    { params: status ? { status } : undefined },
  );
  return response.data as {
    success: boolean;
    data: import('./claimUploadBatchTypes').ClaimUploadBatchSummary[];
  };
};

export const adminGetClaimUploadBatch = async (orgId: string, batchId: string) => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/upload-claims/batches/${batchId}`,
  );
  return response.data as {
    success: boolean;
    data: import('./claimUploadBatchTypes').ClaimUploadBatchDetail;
  };
};

export const adminDeleteClaimUploadBatch = async (orgId: string, batchId: string) => {
  const response = await adminApi.delete(
    `/admin/organizations/${orgId}/upload-claims/batches/${batchId}`,
  );
  return response.data as { success: boolean; message: string };
};

export const adminDownloadClaimUploadBatchFile = async (
  orgId: string,
  batchId: string,
): Promise<Blob> => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/upload-claims/batches/${batchId}/file`,
    { responseType: 'blob' },
  );
  return response.data as Blob;
};

export const adminUpdateClaimUploadBatchMapping = async (
  orgId: string,
  batchId: string,
  body: {
    mapping: Record<string, string>;
    save_preset?: boolean;
    preset_name?: string;
    auto_apply?: boolean;
  },
) => {
  const response = await adminApi.patch(
    `/admin/organizations/${orgId}/upload-claims/batches/${batchId}/mapping`,
    body,
  );
  return response.data;
};

export const adminProcessClaimUploadBatch = async (
  orgId: string,
  batchId: string,
  options: AdminBulkUploadOptions & { file?: File } = {},
) => {
  const form = new FormData();
  if (options.mappingOverrides && Object.keys(options.mappingOverrides).length > 0) {
    form.append('mapping_overrides', JSON.stringify(options.mappingOverrides));
  }
  if (options.dryRun) {
    form.append('dry_run', 'true');
  }
  if (options.file) {
    form.append('file', options.file);
  }
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/upload-claims/batches/${batchId}/process`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Large batches process synchronously server-side; allow up to 5 min.
      timeout: 300000,
    },
  );
  return response.data as BulkUploadClaimsResponse;
};

export interface AdminClaimStatusLabel {
  slug: string;
  label: string;
  color: string;
  usage_count: number;
}

export const adminListClaimStatusLabels = async (
  orgId: string,
): Promise<AdminClaimStatusLabel[]> => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/claims/status-labels`,
  );
  return (response.data?.data ?? []) as AdminClaimStatusLabel[];
};

export const adminListClaimUploadMappingPresets = async (
  orgId: string,
  headersHash?: string,
) => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/upload-claims/mapping-presets`,
    { params: headersHash ? { headers_hash: headersHash } : undefined },
  );
  return response.data as {
    success: boolean;
    data: import('./claimUploadBatchTypes').ClaimUploadMappingPreset[];
  };
};

// (Removed in 062 cleanup) /admin/organizations/{id}/operations and
// /admin/organizations/{id}/tools were retired with the operations
// policy / RCM tools per-org configuration tables. The
// `knowledge_share_enabled` opt-in now lives on the Claim Fields
// schema response (see ClaimFieldSchema in `./types`).


// ---------------------------------------------------------------------------
// Live Calls dashboard (admin Phase 1+2). Postgres-backed; FE polls.
// Phase 3 (live audio listen-in) will add a WS endpoint backed by Redis.
// ---------------------------------------------------------------------------

export interface AdminLiveCallRow {
  call_id: string;
  org_id: string | null;
  org_name: string | null;
  twilio_call_sid: string | null;
  started_at: string | null;
  ended_at: string | null;
  ended_reason: string | null;
  duration_secs: number | null;
  agent_name: string | null;
  call_mode: string | null;
  claim_id: string | null;
  claim_number: string | null;
  patient_name: string | null;
  payer_name: string | null;
  payer_phone: string | null;
  current_node_id: string | null;
  current_node_name: string | null;
  hold_active: boolean;
  last_speaker: 'user' | 'agent' | string | null;
  last_transcript_preview: string | null;
  transcript_turn_count: number;
  tool_call_count: number;
  worker_pid: number | null;
  updated_at: string | null;
}

export interface AdminLiveCallListResponse {
  items: AdminLiveCallRow[];
  total: number;
  fetched_at: string;
}

// Constrained to the event_type strings the publisher emits today.
// Kept open (string fallback) because new event kinds can land
// without a frontend deploy.
export type AdminLiveCallEventType =
  | 'call_started'
  | 'call_ended'
  | 'transcript'
  | 'tool'
  | 'transition'
  | 'hold_enter'
  | 'hold_exit'
  | string;

export interface AdminLiveCallEvent {
  id: number;
  call_id: string;
  event_type: AdminLiveCallEventType;
  speaker: 'user' | 'agent' | string | null;
  payload: Record<string, unknown>;
  occurred_at: string | null;
}

export interface AdminLiveCallEventsResponse {
  items: AdminLiveCallEvent[];
  count: number;
  max_id: number;
}

export const listAdminLiveCalls = async (params?: {
  org_id?: string;
  include_ended?: boolean;
  limit?: number;
}): Promise<AdminLiveCallListResponse> => {
  const response = await adminApi.get('/admin/live-calls', { params });
  return response.data;
};

export const getAdminLiveCall = async (callId: string): Promise<AdminLiveCallRow> => {
  const response = await adminApi.get(`/admin/live-calls/${callId}`);
  return response.data;
};

export const getAdminLiveCallEvents = async (
  callId: string,
  params?: { since_id?: number; limit?: number }
): Promise<AdminLiveCallEventsResponse> => {
  const response = await adminApi.get(`/admin/live-calls/${callId}/events`, { params });
  return response.data;
};

// Comprehensive single-call audit dump. Returns call metadata, all
// events, computed test metrics (pass/fail), latency stats, and
// summary counts in one shot. The Export button in
// LiveCallsStreamView calls this and renders a markdown report.
export interface AdminLiveCallAuditExport {
  call: AdminLiveCallRow;
  events: Array<{
    id: number;
    call_id: string;
    event_type: string;
    speaker: string | null;
    payload: Record<string, unknown> | null;
    occurred_at: string | null;
  }>;
  metrics: Record<string, { label: string; status: string; detail: string }>;
  latency: {
    first_audio_render_ms: number | null;
    user_to_agent_transcript_ms: {
      count: number;
      p50: number | null;
      p90: number | null;
      p99: number | null;
      max: number | null;
      samples: number[];
    };
    turn_latency_perceived_ms: {
      count: number;
      p50: number | null;
      p90: number | null;
      p99: number | null;
      max: number | null;
      samples: number[];
    };
  };
  summary: {
    event_total: number;
    transcript_turn_count: number;
    tool_call_count: number;
    diagnostic_count: number;
    by_diagnostic_kind: Record<string, number>;
  };
  exported_at: string;
}

export const getAdminLiveCallAuditExport = async (
  callId: string,
): Promise<AdminLiveCallAuditExport> => {
  const response = await adminApi.get(
    `/admin/live-calls/${callId}/audit-export`,
  );
  return response.data;
};


// ---------------------------------------------------------------------------
// UI overhaul (documentation.md §19h): admin master-org-dropdown surface.
// These mirror the org-scoped user APIs (`/patients`, `/payers`,
// `/providers`, `/calls`) but are scoped by URL `org_id` instead of
// the JWT, so the admin can view/manage data for the org currently
// selected in the topbar without impersonating a user.
// ---------------------------------------------------------------------------

export interface AdminSidebarCounts {
  claims: number;
  calls: number;
  patients: number;
  payers: number;
  providers: number;
}

export const getAdminSidebarCounts = async (orgId: string): Promise<AdminSidebarCounts> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/sidebar-counts`)
  );
  return (
    response.data?.data ?? {
      claims: 0,
      calls: 0,
      patients: 0,
      payers: 0,
      providers: 0,
    }
  );
};

// ---- Admin Patients ----

export const adminListOrgPatients = async (
  orgId: string,
  params?: { page?: number; limit?: number; search_term?: string | null }
): Promise<GetPatientsResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/patients`, { params })
  );
  return response.data;
};

export const adminCreateOrgPatient = async (
  orgId: string,
  data: CreatePatientRequest
): Promise<CreatePatientResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/patients`,
    data
  );
  return response.data;
};

export const adminUpdateOrgPatient = async (
  orgId: string,
  patientId: string,
  data: UpdatePatientRequest
): Promise<CreatePatientResponse> => {
  const response = await adminApi.put(
    `/admin/organizations/${orgId}/patients/${patientId}`,
    data
  );
  return response.data;
};

export const adminDeleteOrgPatient = async (
  orgId: string,
  patientId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await adminApi.delete(
    `/admin/organizations/${orgId}/patients/${patientId}`
  );
  return response.data;
};

export interface AdminBulkDeleteResponse {
  success: boolean;
  deleted: number;
  deleted_ids: string[];
  failed: Array<{ id: string; error: string }>;
}

export const adminBulkDeleteOrgPatients = async (
  orgId: string,
  params: { ids?: string[]; all?: boolean; search_term?: string | null }
): Promise<AdminBulkDeleteResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/patients/bulk-delete`,
    params
  );
  return response.data;
};

// ---- Admin Payers ----

export const adminListOrgPayers = async (
  orgId: string,
  params?: { page?: number; limit?: number; search_term?: string | null }
): Promise<GetPayersResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/payers`, { params })
  );
  return response.data;
};

export const adminCreateOrgPayer = async (
  orgId: string,
  data: CreatePayerRequest
): Promise<CreatePayerResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/payers`,
    data
  );
  return response.data;
};

export const adminUpdateOrgPayer = async (
  orgId: string,
  payerId: string,
  data: PayerUpdatePayload
): Promise<CreatePayerResponse> => {
  const response = await adminApi.put(
    `/admin/organizations/${orgId}/payers/${payerId}`,
    data
  );
  return response.data;
};

export const adminDeleteOrgPayer = async (
  orgId: string,
  payerId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await adminApi.delete(
    `/admin/organizations/${orgId}/payers/${payerId}`
  );
  return response.data;
};

export const adminBulkDeleteOrgPayers = async (
  orgId: string,
  params: { ids?: string[]; all?: boolean; search_term?: string | null }
): Promise<AdminBulkDeleteResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/payers/bulk-delete`,
    params
  );
  return response.data;
};

// ---- Admin Providers ----

export const adminListOrgProviders = async (
  orgId: string,
  params?: { page?: number; limit?: number; search_term?: string | null }
): Promise<GetProvidersResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/providers`, { params })
  );
  return response.data;
};

export const adminCreateOrgProvider = async (
  orgId: string,
  data: CreateProviderRequest
): Promise<TProviderResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/providers`,
    data
  );
  return response.data;
};

export const adminUpdateOrgProvider = async (
  orgId: string,
  providerId: string,
  data: UpdateProviderRequest
): Promise<TProviderResponse> => {
  const response = await adminApi.put(
    `/admin/organizations/${orgId}/providers/${providerId}`,
    data
  );
  return response.data;
};

export const adminDeleteOrgProvider = async (
  orgId: string,
  providerId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await adminApi.delete(
    `/admin/organizations/${orgId}/providers/${providerId}`
  );
  return response.data;
};

export const adminBulkDeleteOrgProviders = async (
  orgId: string,
  params: { ids?: string[]; all?: boolean; search_term?: string | null }
): Promise<AdminBulkDeleteResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/providers/bulk-delete`,
    params
  );
  return response.data;
};

export const adminLookupNppes = async (
  npi: string
): Promise<import('./types').NppesLookupResponse> => {
  const response = await adminApi.get(`/admin/nppes/lookup/${npi}`);
  return response.data;
};

export interface AdminNppesBackfillChange {
  id: string;
  npi: string;
  old_name: string;
  new_name: string;
  enumeration_type: string;
}

export interface AdminNppesBackfillResponse {
  success: boolean;
  applied: boolean;
  scanned: number;
  unchanged: number;
  skipped: number;
  changes: AdminNppesBackfillChange[];
  skipped_details: Array<{ id: string; reason: string }>;
  // Server returns either ``would_change`` (dry run) or ``changed``
  // (apply); we widen the type so the same call site handles both.
  would_change?: number;
  changed?: number;
}

export const adminVerifyOrgProvidersAgainstNppes = async (
  orgId: string,
  apply: boolean
): Promise<AdminNppesBackfillResponse> => {
  const response = await adminApi.post(
    `/admin/organizations/${orgId}/providers/verify-against-nppes`,
    { apply }
  );
  return response.data;
};

// ---- Admin Call Logs (read-only) ----

export const adminListOrgCalls = async (
  orgId: string,
  params?: {
    page?: number;
    limit?: number;
    search_term?: string | null;
    claim_id?: string | undefined;
    outcome?: string | undefined;
  }
): Promise<GetCallsResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/calls`, { params })
  );
  return response.data;
};

export const adminGetOrgCallTranscriptUrl = async (
  orgId: string,
  callId: string
): Promise<{ success: boolean; data: { transcript_url: string; expires_in: number } }> => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/calls/${callId}/transcript`
  );
  return response.data;
};

export const adminGetOrgCallAudioUrl = async (
  orgId: string,
  callId: string
): Promise<{ success: boolean; data: { audio_url: string; expires_in: number } }> => {
  const response = await adminApi.get(
    `/admin/organizations/${orgId}/calls/${callId}/audio`
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Admin EDI / Stedi
// Backed by app/controller/admin_edi_controller.py.
// ---------------------------------------------------------------------------

export interface AdminEdiRatesData {
  org_id: string;
  eligibility_cents: number | null;
  claim_status_cents: number | null;
  era_cents: number | null;
  cob_cents: number | null;
  discovery_cents: number | null;
  submission_cents: number | null;
  voice_auto_dial_enabled: boolean;
  edi_first_routing_enabled: boolean;
}

export interface AdminEdiRatesDefaults {
  eligibility_cents: number;
  claim_status_cents: number;
  era_cents: number;
  cob_cents: number;
  discovery_cents: number;
  submission_cents: number;
}

export interface AdminEdiRatesResponse {
  success: boolean;
  message: string;
  data: AdminEdiRatesData;
  defaults: AdminEdiRatesDefaults;
}

export interface AdminEdiRatesUpdateRequest {
  eligibility_cents?: number | null;
  claim_status_cents?: number | null;
  era_cents?: number | null;
  cob_cents?: number | null;
  discovery_cents?: number | null;
  submission_cents?: number | null;
  voice_auto_dial_enabled?: boolean;
  edi_first_routing_enabled?: boolean;
}

export interface AdminEdiTransactionRow {
  id: string;
  org_id: string;
  claim_id?: string | null;
  payer_id?: string | null;
  transaction_type: string;
  stedi_transaction_id?: string | null;
  billable: boolean;
  customer_charge_cents: number;
  internal_cogs_cents?: number | null;
  http_status?: number | null;
  latency_ms?: number | null;
  error_message?: string | null;
  created_at: string;
}

export interface AdminEdiTransactionsResponse {
  success: boolean;
  message: string;
  data: AdminEdiTransactionRow[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminEdiTransactionDetail extends AdminEdiTransactionRow {
  request_payload?: Record<string, unknown> | null;
  response_payload?: Record<string, unknown> | null;
  http_trace?: Record<string, unknown> | null;
  stedi_correlation_id?: string | null;
  canonical_claim_id?: string | null;
}

export interface AdminEdiTransactionDetailResponse {
  success: boolean;
  message: string;
  data: AdminEdiTransactionDetail;
}

export interface AdminEdiSummaryData {
  total_last_7d: number;
  total_last_30d: number;
  success_rate_7d: number;
  billed_cents_7d: number;
  billed_cents_30d: number;
  cogs_cents_30d: number;
  last_transaction_at: string | null;
  by_type_30d: Record<string, number>;
}

export interface AdminEdiSummaryResponse {
  success: boolean;
  message: string;
  data: AdminEdiSummaryData;
}

export const adminGetOrgEdiRates = async (
  orgId: string
): Promise<AdminEdiRatesResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/edi/rates`)
  );
  return response.data;
};

export const adminUpdateOrgEdiRates = async (
  orgId: string,
  body: AdminEdiRatesUpdateRequest
): Promise<AdminEdiRatesResponse> => {
  const response = await adminApi.put(
    `/admin/organizations/${orgId}/edi/rates`,
    body
  );
  return response.data;
};

export const adminListOrgEdiTransactions = async (
  orgId: string,
  params?: {
    page?: number;
    limit?: number;
    transaction_type?: string;
    claim_id?: string;
    billable?: boolean;
  }
): Promise<AdminEdiTransactionsResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/edi/transactions`, { params })
  );
  return response.data;
};

export const adminListOrgEraInbox = async (
  orgId: string,
  params?: { page?: number; limit?: number }
): Promise<AdminEdiTransactionsResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/edi/era-inbox`, { params })
  );
  return response.data;
};

export const adminGetOrgEdiTransaction = async (
  orgId: string,
  transactionId: string
): Promise<AdminEdiTransactionDetailResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/edi/transactions/${transactionId}`)
  );
  return response.data;
};

export const adminGetOrgEdiSummary = async (
  orgId: string
): Promise<AdminEdiSummaryResponse> => {
  const response = await withGetRetry(() =>
    adminApi.get(`/admin/organizations/${orgId}/edi/summary`)
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Admin-initiated EDI checks (debit the org's credits)
//
// Wraps POST /admin/organizations/{orgId}/edi/{eligibility-check,claim-status-check}.
// Same return shape as the tenant ehrApi.runEdi*Check helpers so the
// ClaimDetailPanel can switch between the two client functions
// without restructuring its result handling. See admin_edi_controller.py.
// ---------------------------------------------------------------------------

export interface AdminEdiCheckResult {
  ledger_id?: string;
  charge_cents?: number;
  http_status?: number;
  latency_ms?: number;
  error?: string;
  billable?: boolean;
  mapped_status?: string;
  snapshot?: Record<string, unknown> | null;
  [k: string]: unknown;
}

export interface AdminEdiCheckEnvelope {
  success: boolean;
  message: string;
  data: AdminEdiCheckResult | null;
}

export const adminRunOrgEligibilityCheck = async (
  orgId: string,
  claimId: string,
  dateOfService?: string
): Promise<AdminEdiCheckResult> => {
  const response = await adminApi.post<AdminEdiCheckEnvelope>(
    `/admin/organizations/${orgId}/edi/eligibility-check`,
    { claim_id: claimId, date_of_service: dateOfService }
  );
  return (response.data?.data ?? {}) as AdminEdiCheckResult;
};

export const adminRunOrgClaimStatusCheck = async (
  orgId: string,
  claimId: string
): Promise<AdminEdiCheckResult> => {
  const response = await adminApi.post<AdminEdiCheckEnvelope>(
    `/admin/organizations/${orgId}/edi/claim-status-check`,
    { claim_id: claimId }
  );
  return (response.data?.data ?? {}) as AdminEdiCheckResult;
};

export const adminEndLiveCall = async (callId: string): Promise<{ success: boolean; message: string }> => {
  const response = await adminApi.post(`/admin/live-calls/${callId}/end`);
  return response.data;
};

export const adminRunDelegatedProbe = async (
  orgId: string,
  params: {
    claim_id: string;
    target_stedi_id: string;
    target_payer_name: string;
    strip_member_id_prefix?: string;
  }
): Promise<AdminEdiCheckResult> => {
  const response = await adminApi.post<AdminEdiCheckEnvelope>(
    `/admin/organizations/${orgId}/edi/delegated-probe`,
    params
  );
  return (response.data?.data ?? {}) as AdminEdiCheckResult;
};

interface AdminInsightsResponsesEnvelope {
  success: boolean;
  message: string;
  data: StediMultiExportFile;
}

/** Latest Stedi claim-status + eligibility payloads for an org (admin overview). */
export const getAdminOrgInsightsResponses = async (orgId: string): Promise<StediMultiExportFile> => {
  const response = await withGetRetry(() =>
    adminApi.get<AdminInsightsResponsesEnvelope>(`/admin/organizations/${orgId}/insights/responses`)
  );
  return response.data.data;
};
